import { z } from 'zod';
import { IsoDateTimeSchema } from './common/iso-date.js';
// "Hora por confirmar" (handover F, Part 1). A session can have its day fixed
// and its hour pending: the operator sets it later (the tide decides it). While
// pending, the session stores a PROVISIONAL start — the start of its part of
// the day — so every deadline that counts from `starts_at` (cutoff, reminders,
// free cancellation) has a defined value. Setting the hour replaces the start
// and recalculates them; the provisional start is kept as a floor so nobody's
// free-cancellation deadline gets shorter than the one already told to them.
export const SESSION_DAY_PARTS = ['morning', 'afternoon', 'unspecified'];
export const SessionDayPartSchema = z.enum(SESSION_DAY_PARTS);
// The provisional local start of each part of the day. "Sin precisar" uses
// the morning's: the earliest plausible start keeps every deadline on the safe
// side (a cutoff never closes later than it could, a free window is never
// promised past a start that turns out earlier).
export const PROVISIONAL_START_TIMES = {
    morning: '08:00',
    afternoon: '15:00',
    unspecified: '08:00',
};
// How many days before the session the operator promises, at the latest, to
// announce its hour. 0 = the same day.
export const HOUR_NOTICE_DAYS_MAX = 30;
export const HourNoticeDaysSchema = z.number().int().min(0).max(HOUR_NOTICE_DAYS_MAX);
// The pending-hour part of a schedule rule or pattern. `null` = a fixed hour.
export const PendingHourSchema = z.object({
    dayPart: SessionDayPartSchema,
    noticeDays: HourNoticeDaysSchema,
});
// The calendar date (YYYY-MM-DD) by which the hour is announced: the
// session's local date minus the notice. Pure date arithmetic on the calendar
// day — no timezone involved, the input is already local.
export const hourAnnounceByDate = (localDate, noticeDays) => {
    const [year, month, day] = localDate.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined) {
        throw new Error(`hourAnnounceByDate: not a YYYY-MM-DD date: ${localDate}`);
    }
    const utc = Date.UTC(year, month - 1, day - noticeDays);
    return new Date(utc).toISOString().slice(0, 10);
};
// How long after the "ya hay hora" email a customer can still cancel free,
// whatever the activity's tier (handover F §5): 24 hours, never past the
// session's start.
export const HOUR_SET_FREE_CANCEL_HOURS = 24;
// The start every refund counts from: the later of the real start and the
// provisional one the customer booked against. Setting the hour earlier than
// the provisional start must never shorten a free-cancellation window that was
// already promised; setting it later lengthens it, which is fine.
export const refundReferenceStartsAt = (startsAt, provisionalStartsAt) => provisionalStartsAt !== null && provisionalStartsAt.getTime() > startsAt.getTime()
    ? provisionalStartsAt
    : startsAt;
// What a reader of a session needs to know about a pending hour: the part of
// the day and the local date (YYYY-MM-DD) by which it will be announced. `null`
// on a session = its hour is set.
export const SessionPendingHourInfoSchema = z.object({
    dayPart: SessionDayPartSchema,
    announceBy: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'YYYY-MM-DD' }),
});
// The row → info mapping, in one place for every reader. The CHECK
// sessions_pending_hour_chk guarantees both columns are set while pending.
export const sessionPendingHourInfo = (row) => row.hourPending && row.dayPart !== null && row.hourAnnounceBy !== null
    ? { dayPart: row.dayPart, announceBy: row.hourAnnounceBy }
    : null;
// --- Printing a pending session's time (handover Y) ------------------------
// The provisional start is never shown as a time: not to a customer, not to
// the operator, not by the assistant. Wherever a pending session's hour would
// go, these words go instead. Every surface (emails, pages, widget, panel,
// avisos, assistant) takes its wording from here, so the phrase cannot drift.
const PENDING_HOUR_WORDS = {
    es: { morning: 'por la mañana', afternoon: 'por la tarde', toConfirm: 'hora por confirmar' },
    en: {
        morning: 'in the morning',
        afternoon: 'in the afternoon',
        toConfirm: 'time to be confirmed',
    },
};
// "por la mañana" / "por la tarde"; null for "sin precisar", which has no part
// of the day to name.
export const dayPartWords = (dayPart, language) => dayPart === 'unspecified' ? null : PENDING_HOUR_WORDS[language][dayPart];
// "por la mañana · hora por confirmar" / "hora por confirmar" (lower case: it
// stands where "10:00" would, usually after the day). `capitalise` for the
// start of a line: "Por la mañana · hora por confirmar".
export const pendingHourPhrase = (dayPart, language, options = {}) => {
    const words = PENDING_HOUR_WORDS[language];
    const part = dayPartWords(dayPart, language);
    const phrase = part === null ? words.toConfirm : `${part} · ${words.toConfirm}`;
    return options.capitalise === true ? phrase.charAt(0).toUpperCase() + phrase.slice(1) : phrase;
};
// The time slot of a session line: its formatted hour, or — while the hour is
// pending — the phrase above. `formatTime` is the caller's own clock format
// (each surface already has one, "10:00" / "10:00 am").
export const sessionTimeOrPending = (session, language, formatTime, options = {}) => session.pendingHour === null
    ? formatTime(session.startsAt)
    : pendingHourPhrase(session.pendingHour.dayPart, language, options);
// --- Setting the hour (operator) -------------------------------------------
// POST /operators/:operatorId/sessions/:sessionId/announce-hour. The hour
// must fall on the session's own day; setting it can also change the place
// (one action, one email). `meetingPoint` omitted = keep the place.
export const SESSION_MEETING_POINT_MAX = 200;
export const AnnounceSessionHourRequestSchema = z.object({
    startsAt: IsoDateTimeSchema,
    meetingPoint: z.string().trim().min(1).max(SESSION_MEETING_POINT_MAX).nullable().optional(),
});
export const AnnounceSessionHourResponseSchema = z.object({
    data: z.object({
        sessionId: z.string().uuid(),
        startsAt: IsoDateTimeSchema,
        // The bookings whose customer is emailed the hour.
        notifiedBookingIds: z.array(z.string().uuid()),
    }),
});
// The deadline the customer is told in the "ya hay hora" email: the later of
// the window granted by the email itself and the activity's own free window
// (the start minus the widest 100 % tier). Pure.
export const fullRefundDeadline = (tiers, finalRefundPercent, referenceStartsAt) => {
    if (finalRefundPercent >= 100)
        return referenceStartsAt;
    const widestFree = tiers
        .filter((tier) => tier.refundPercent >= 100)
        .reduce((widest, tier) => (widest === null || tier.thresholdHours > widest ? tier.thresholdHours : widest), null);
    return widestFree === null
        ? null
        : new Date(referenceStartsAt.getTime() - widestFree * 60 * 60 * 1000);
};
//# sourceMappingURL=session-hour.js.map